// OWNER: AREA-EVENTS (event-types/*, messages/*, requests/*). Components go in src/features/events.
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Search, X, Zap } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { DataTable, tableColumns, type DataTableColumn } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { RelativeTime } from '@/components/RelativeTime'
import { Button, IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Segmented } from '@/components/ui/segmented'
import { useEventTypesList } from '@/features/events/api'
import { AppName } from '@/features/events/AppName'
import { CreateEventTypeDialog } from '@/features/events/CreateEventTypeDialog'
import { dispatchLabel, EnabledBadge, ValidationBadge } from '@/features/events/labels'
import type { EventTypeSummary } from '@/features/events/types'
import { ProposalCountBadge } from '@/features/proposals/ProposalCountBadge'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useAppScope } from '@/shell/useAppScope'

type EnabledFilter = 'all' | 'enabled' | 'disabled'

interface EventTypesSearch {
  q?: string
  status?: Exclude<EnabledFilter, 'all'>
  new?: boolean
}

export const Route = createFileRoute('/event-types/')({
  validateSearch: (s: Record<string, unknown>): EventTypesSearch => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    status: s.status === 'enabled' || s.status === 'disabled' ? s.status : undefined,
    new: s.new === true || s.new === 'true' || s.new === 1 ? true : undefined,
  }),
  component: EventTypesPage,
})

const col = tableColumns<EventTypeSummary>()

function buildColumns(showApp: boolean): DataTableColumn<EventTypeSummary>[] {
  return col.columns([
    col.accessor('key', {
      header: 'Event',
      cell: (c) => (
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono text-[13px] font-medium text-ink">
              {c.getValue()}
            </span>
            <ProposalCountBadge
              count={c.row.original.open_proposals}
              to={{ kind: 'event_type', id: c.row.original.id }}
            />
          </div>
          <div className="truncate text-xs text-ink-2">{c.row.original.name}</div>
        </div>
      ),
      enableHiding: false,
    }),
    ...(showApp
      ? [
          col.accessor('application_id', {
            id: 'application',
            header: 'Application',
            cell: (c) => <AppName id={c.getValue()} />,
            meta: { width: '11rem' },
          }),
        ]
      : []),
    col.accessor('rule_count', {
      header: 'Rules',
      cell: (c) =>
        c.getValue() === 0 ? (
          <span className="text-warning">None</span>
        ) : (
          <span className="tabular">{c.getValue()}</span>
        ),
      meta: { align: 'right', width: '5rem' },
    }),
    col.accessor('dispatch', {
      header: 'Dispatch',
      cell: (c) => <span className="text-ink-2">{dispatchLabel(c.getValue())}</span>,
      meta: { width: '8rem' },
    }),
    col.accessor('validation', {
      header: 'Validation',
      cell: (c) => (
        <ValidationBadge mode={c.getValue()} hasSchema={c.row.original.schema != null} />
      ),
      meta: { width: '7rem' },
    }),
    col.accessor((e) => e.samples.length, {
      id: 'samples',
      header: 'Samples',
      cell: (c) => <span className="tabular text-ink-2">{c.getValue()}</span>,
      meta: { align: 'right', width: '6rem' },
    }),
    col.accessor('enabled', {
      header: 'Status',
      cell: (c) => <EnabledBadge enabled={c.getValue()} />,
      meta: { width: '7rem' },
    }),
    col.accessor('updated_at', {
      header: 'Updated',
      cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
      sortFn: 'datetime',
      meta: { align: 'right', width: '8rem' },
    }),
  ])
}

const ENABLED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
] as const

function EventTypesPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const { appId, application } = useAppScope()
  const { can } = usePermissions()
  const [text, setText] = useState(search.q ?? '')
  const query = useDebouncedValue(text.trim(), 250)
  const enabledFilter: EnabledFilter = search.status ?? 'all'
  const creating = !!search.new
  const setCreating = (open: boolean) =>
    void navigate({ search: (prev) => ({ ...prev, new: open || undefined }), replace: true })

  const list = useEventTypesList({
    app: appId,
    q: query || undefined,
    enabled: enabledFilter === 'all' ? undefined : enabledFilter === 'enabled',
  })
  const columns = useMemo(() => buildColumns(!appId), [appId])

  useEffect(() => {
    if ((search.q ?? '') !== query)
      void navigate({ search: (prev) => ({ ...prev, q: query || undefined }), replace: true })
  }, [search.q, query, navigate])

  const filtered = !!query || enabledFilter !== 'all'
  const newButton = (
    <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
      New event type
    </Button>
  )

  return (
    <>
      <PageHeader
        title="Event types"
        description={
          application
            ? `Events ${application.name} posts, and the rules that turn each one into messages.`
            : 'The events upstream applications post, and the rules that turn each one into messages.'
        }
        actions={
          <Can permission="edit_content" mode="disable">
            {newButton}
          </Can>
        }
      />
      <DataTable
        aria-label="Event types"
        columns={columns}
        data={list.items}
        getRowId={(e) => e.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        pagination={list.pagination}
        persistKey="event-types"
        initialSorting={[{ id: 'key', desc: false }]}
        onRowClick={(e) =>
          void navigate({ to: '/event-types/$eventTypeId', params: { eventTypeId: e.id } })
        }
        toolbar={
          <>
            <Input
              className="w-72"
              size="sm"
              placeholder="Search by key or name"
              aria-label="Search event types"
              value={text}
              onChange={(e) => setText(e.target.value)}
              leading={<Search />}
              trailing={
                text ? (
                  <IconButton
                    label="Clear search"
                    size="xs"
                    tooltip={false}
                    onClick={() => setText('')}
                  >
                    <X />
                  </IconButton>
                ) : undefined
              }
            />
            <Segmented
              aria-label="Filter by status"
              value={enabledFilter}
              options={ENABLED_OPTIONS}
              onValueChange={(v) =>
                void navigate({
                  search: (prev) => ({ ...prev, status: v === 'all' ? undefined : v }),
                  replace: true,
                })
              }
            />
          </>
        }
        empty={
          filtered ? (
            <EmptyState
              compact
              icon={Search}
              title="No matching event types"
              description="Try a different search or status filter."
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    setText('')
                    void navigate({
                      search: (prev) => ({ ...prev, q: undefined, status: undefined }),
                      replace: true,
                    })
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Zap}
              title={
                application ? `${application.name} has no event types yet` : 'No event types yet'
              }
              description="An event type declares something an application posts, such as order.shipped. Its rules decide which template goes to whom."
              action={can('edit_content') ? newButton : undefined}
            />
          )
        }
      />
      <CreateEventTypeDialog open={creating} onOpenChange={setCreating} defaultAppId={appId} />
    </>
  )
}
